'use client';

import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import VisibilityReport from '@/components/visibility/VisibilityReport';
import type { VisibilityCheck, VisibilityResult } from '@/lib/visibility/analyse';
import type { EntityPreview } from '@/lib/visibility/preview';

interface ApiOk {
  ok: true;
  url: string;
  finalUrl: string;
  host: string;
  status: number;
  score: number;
  checks: VisibilityCheck[];
  suggestedSchema: string;
  preview: EntityPreview;
}

interface ApiErr {
  ok: false;
  code: string;
  message: string;
}

export default function VisibilityChecker() {
  const [url, setUrl] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<VisibilityResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;

    setState('loading');
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/api/visibility-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
      });

      const payload = (await response.json()) as ApiOk | ApiErr;

      if (!payload.ok) {
        setState('error');
        setError(payload.message);
        return;
      }

      setResult({ ...payload, markdown: '' });
      setState('done');
    } catch {
      setState('error');
      setError('Something went wrong on our side. Please try again in a moment.');
    }
  };

  const startOver = () => {
    setState('idle');
    setResult(null);
    setError(null);
    setUrl('');
  };

  if (state === 'done' && result) {
    return (
      <div className="mt-8">
        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={startOver}
            className="text-sm font-medium text-slate-600 underline hover:text-slate-900"
          >
            Check a different website
          </button>
        </div>
        <VisibilityReport result={result} />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <form onSubmit={run} className="flex flex-col gap-3 sm:flex-row">
        <label htmlFor="visibility-url" className="sr-only">
          Your website address
        </label>
        <input
          id="visibility-url"
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="yourbusiness.co.za"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          // Not novalidate: the browser's own URL hint is a genuine help for a non-technical
          // user, and we validate properly server-side regardless.
          required
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none focus:border-[#001489] focus:ring-2 focus:ring-[#001489]/30"
        />
        <button
          type="submit"
          disabled={state === 'loading'}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#001489] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#001166] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {state === 'loading' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking
            </>
          ) : (
            <>
              <Search className="h-4 w-4" />
              Check my website
            </>
          )}
        </button>
      </form>

      {state === 'loading' ? (
        <p className="mt-4 text-sm text-slate-600" aria-live="polite">
          Fetching your page and checking {15} things search engines and AI assistants look
          for. This takes a few seconds.
        </p>
      ) : null}

      {state === 'error' && error ? (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
        </div>
      ) : null}

      <p className="mt-3 text-xs text-slate-500">
        We fetch your homepage only. Nothing is stored, and we never email you unless you ask.
      </p>
    </div>
  );
}